import { Injectable } from '@angular/core';
import { LoginService, StorageService } from '@uoa/auth';
import { environment } from 'src/environments/environment';
import { CatchitEnvironment } from '../interfaces/environment.interface';

export class InvalidCredentialsError extends Error {}
export class InvalidPasswordError extends Error {}

export type ExternalLoginResult =
  | { status: 'success' }
  | { status: 'newPasswordRequired'; session: string; username: string };

/**
 * 'refreshed' - new tokens stored. 'rejected' - no refresh token, or Cognito refused it
 * (expired/revoked); tokens cleared. 'unavailable' - couldn't reach Cognito (offline, 5xx);
 * tokens left untouched so a later attempt can still succeed.
 */
export type SessionRefreshResult = 'refreshed' | 'rejected' | 'unavailable';

/**
 * Logs external (non-UoA) users in directly with an email/password form,
 * bypassing the Cognito Hosted UI and the UoATestIDP SAML federation entirely.
 *
 * Calls Cognito's InitiateAuth/RespondToAuthChallenge APIs (USER_PASSWORD_AUTH
 * flow) directly - these are AWS-documented, unauthenticated public client
 * actions, no SDK or request signing required. Requires
 * "ALLOW_USER_PASSWORD_AUTH" to be enabled as an allowed auth flow on the app
 * client in the Cognito console.
 *
 * Admin-created users get a temporary password and must set a new one on
 * first login - Cognito signals this via a NEW_PASSWORD_REQUIRED challenge
 * (a Session token, no tokens yet) instead of completing the sign-in. Callers
 * must check ExternalLoginResult.status and, if 'newPasswordRequired', collect
 * a new password from the user and call completeNewPassword(). Other
 * challenge types (MFA, required attributes) aren't handled - not needed yet.
 *
 * On success, tokens are written to the same StorageService keys @uoa/auth's
 * own AuthService uses (accessToken/idToken/refreshToken/expiresAt), then
 * LoginService.isAuthenticated() is called to sync @uoa/auth's loggedIn$/
 * userInfo$ from them - so the rest of the app (CatchitUserService, guards,
 * etc.) behaves identically regardless of which login path was used.
 *
 * Note: USER_PASSWORD_AUTH sends the password to Cognito directly (over TLS)
 * rather than using SRP, which never transmits the password itself. This is
 * the simpler, AWS-supported tradeoff for a plain email/password form without
 * pulling in an SRP-capable client library.
 */
@Injectable({
  providedIn: 'root'
})
export class ExternalLoginService {
  private readonly env = environment as CatchitEnvironment;
  private refreshInFlight: Promise<SessionRefreshResult> | null = null;

  constructor(
    private storageService: StorageService,
    private loginService: LoginService
  ) {}

  async login(username: string, password: string): Promise<ExternalLoginResult> {
    const data = await this.callCognitoIdp('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: this.env.auth.cognitoClientId,
      AuthParameters: { USERNAME: username, PASSWORD: password }
    });
    return this.handleChallengeResponse(data, username);
  }

  async completeNewPassword(username: string, session: string, newPassword: string): Promise<ExternalLoginResult> {
    const data = await this.callCognitoIdp('RespondToAuthChallenge', {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: this.env.auth.cognitoClientId,
      Session: session,
      ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword }
    });
    return this.handleChallengeResponse(data, username);
  }

  /**
   * Requests a password reset code via Cognito's ForgotPassword action. Deliberately
   * swallows "user doesn't exist" so the caller can show the same message either way -
   * revealing that distinction is a user-enumeration risk. Other errors (e.g. rate
   * limiting) still propagate.
   */
  async forgotPassword(username: string): Promise<void> {
    try {
      await this.callCognitoIdp('ForgotPassword', {
        ClientId: this.env.auth.cognitoClientId,
        Username: username
      });
    } catch (error) {
      if (error instanceof InvalidCredentialsError) {
        return;
      }
      throw error;
    }
  }

  /** Completes a password reset with the code sent by forgotPassword(). */
  async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
    await this.callCognitoIdp('ConfirmForgotPassword', {
      ClientId: this.env.auth.cognitoClientId,
      Username: username,
      ConfirmationCode: code,
      Password: newPassword
    });
  }

  /**
   * Changes the current, already-logged-in user's own password via Cognito's ChangePassword
   * action - takes the caller's access token plus their current password as proof of
   * identity, no verification code involved (that's ForgotPassword, a different flow for
   * when the user doesn't know their current password at all). Cognito itself rejects an
   * incorrect current password (NotAuthorizedException) or an expired/invalid session
   * (also NotAuthorizedException) - both surface as InvalidCredentialsError.
   */
  async changePassword(existingPassword: string, newPassword: string): Promise<void> {
    const accessToken = await this.storageService.getItem('accessToken');
    await this.callCognitoIdp('ChangePassword', {
      AccessToken: accessToken,
      PreviousPassword: existingPassword,
      ProposedPassword: newPassword
    });
  }

  /**
   * Exchanges the stored refresh token for new access/ID tokens via Cognito's
   * REFRESH_TOKEN_AUTH flow. Works for both login paths - SSO (Hosted UI) and native
   * email/password sessions are issued by the same app client.
   *
   * Exists because @uoa/auth's LoginService.isAuthenticated() only checks expiresAt and
   * never uses the refresh token, so RequireAuthGuard would otherwise send every user to
   * /login 60 minutes after logging in despite a valid 30-day refresh token (confirmed live
   * on prd). @uoa/auth's own refresh (AuthService._exchangeRefreshTokenForTokens) isn't
   * reused because on any failure - including just being offline - it wipes all tokens and
   * redirects to the Cognito Hosted UI.
   *
   * Concurrent callers (a guard plus several API calls all hitting the same expired token)
   * share one in-flight request rather than each spending the refresh token separately.
   */
  refreshSession(): Promise<SessionRefreshResult> {
    this.refreshInFlight ??= this.doRefreshSession().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefreshSession(): Promise<SessionRefreshResult> {
    const refreshToken = await this.storageService.getItem('refreshToken');
    if (!refreshToken) {
      return 'rejected';
    }

    let data: any;
    try {
      data = await this.callCognitoIdp('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.env.auth.cognitoClientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken }
      });
    } catch (error) {
      if (error instanceof InvalidCredentialsError) {
        this.clearTokens();
        return 'rejected';
      }
      // Offline surfaces either as a fetch() TypeError or as the service worker's synthetic
      // 504 (non-JSON body); a genuine Cognito 5xx lands here too.
      return 'unavailable';
    }

    if (!data.AuthenticationResult) {
      return 'unavailable';
    }
    // Cognito only returns a new refresh token when refresh token rotation is enabled -
    // otherwise the existing one stays valid and must be kept.
    await this.storeTokens({
      ...data.AuthenticationResult,
      RefreshToken: data.AuthenticationResult.RefreshToken ?? refreshToken
    });
    return 'refreshed';
  }

  private clearTokens(): void {
    this.storageService.removeItem('accessToken');
    this.storageService.removeItem('idToken');
    this.storageService.removeItem('refreshToken');
    this.storageService.removeItem('expiresAt');
  }

  private async handleChallengeResponse(data: any, username: string): Promise<ExternalLoginResult> {
    if (data.AuthenticationResult) {
      await this.storeTokens(data.AuthenticationResult);
      return { status: 'success' };
    }
    if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      return { status: 'newPasswordRequired', session: data.Session, username };
    }
    throw new Error(`Additional verification (${data.ChallengeName}) is required. Please contact the CatchIT team.`);
  }

  private async storeTokens(result: any): Promise<void> {
    await this.storageService.setItem('accessToken', result.AccessToken);
    await this.storageService.setItem('idToken', result.IdToken);
    await this.storageService.setItem('refreshToken', result.RefreshToken);
    await this.storageService.setItem('expiresAt', Date.now() + result.ExpiresIn * 1000);

    await this.loginService.isAuthenticated();
  }

  private async callCognitoIdp(action: string, body: object): Promise<any> {
    const response = await fetch(`https://cognito-idp.${this.env.auth.cognitoAwsRegion}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`
      },
      body: JSON.stringify(body)
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.__type === 'NotAuthorizedException' || data.__type === 'UserNotFoundException') {
        throw new InvalidCredentialsError('Incorrect email or password.');
      }
      if (data.__type === 'InvalidPasswordException') {
        throw new InvalidPasswordError(data.message || 'Password does not meet the required policy.');
      }
      throw new Error(data.message || data.__type || 'Login failed.');
    }

    return data;
  }
}
